import { html, TemplateResult, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  ExchangeContentControlAttachmentFilteringRule,
  ExchangeContentControlFilteringRule,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  ExchangeContentControlFormValue,
  ExchangeContentControlSearchableKeywords,
} from '../../../../utils/uiSettings/exchange/exchange-protection.types';
import {
  EXCHANGE_CONTENT_CONTROL_DEFAULT_VALUE,
  getExchangeContentControlSearchableKeywords,
  MAX_CHARS_SUPPORTED,
} from '../../../../utils/uiSettings/exchange/exchange-protection.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzExchangeContentControlAttachmentFilteringRulesGridWebComponent } from '../gridComponents/attachmentFilteringRules/GzExchangeContentControlAttachmentFilteringRulesGridWebComponent';
import { GzExchangeContentControlContentFilteringRulesGridWebComponent } from '../gridComponents/contentFilteringRules/GzExchangeContentControlContentFilteringRulesGridWebComponent';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../ui/gzChipsGeneratorInput';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzTextareaWebComponent } from '../../../../../../../ui/gzInput';
import { validateEmailPattern } from '../../helpers/utils/validators';
import { sortEmailsAndWildcards } from '../../helpers/utils/sorters';
import { SearchableKeyword } from '../../../../../../../ui/gzSearchableAccordionsPanel/types';
import { GzInputWebComponent } from 'apps/webcomponents/src/ui/gzInput/component/GzInputWebComponent';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL)
export class GzExchangeContentControlWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ExchangeContentControlFormValue,
  ExchangeContentControlSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.EXCHANGE_PROTECTION,
    sectionId: SectionId.EXCHANGE_CONTENT_CONTROL,
  };
  protected searchableKeywords = getExchangeContentControlSearchableKeywords();

  @query('#exchange-content-control-enable-field')
  private contentControlEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#exchange-content-control-content')
  private contentControlContent: HTMLElement;
  @query('#exchange-content-control-rules-field')
  private contentControlRulesField: GzFormFieldWebComponent<
    GzExchangeContentControlContentFilteringRulesGridWebComponent,
    ExchangeContentControlFilteringRule[]
  >;
  @query('#exchange-content-filtering-exclusions-senders-field')
  private contentFilteringExclusionsSendersField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#exchange-content-filtering-exclusions-recipients-field')
  private contentFilteringExclusionsRecipientsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#exchange-content-filtering-exclusions-exclude-field')
  private contentFilteringExclusionsExcludeField: GzFormFieldWebComponent<GzCheckboxWebComponent>;

  @query('#exchange-content-control-attachment-enable-field')
  private contentControlAttachmentEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#exchange-content-control-attachment-content')
  private contentControlAttachmentContent: HTMLElement;
  @query('#exchange-content-control-attachment-rules-field')
  private contentControlAttachmentRulesField: GzFormFieldWebComponent<
    GzExchangeContentControlAttachmentFilteringRulesGridWebComponent,
    ExchangeContentControlAttachmentFilteringRule[]
  >;
  @query('#exchange-attachment-filtering-exclusions-senders-field')
  private attachmentFilteringExclusionsSendersField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#exchange-attachment-filtering-exclusions-recipients-field')
  private attachmentFilteringExclusionsRecipientsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#exchange-attachment-filtering-exclusions-exclude-field')
  private attachmentFilteringExclusionsExcludeField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#exchange-attachment-settings-replacement-text-field')
  private attachmentSettingsReplacementTextField: GzFormFieldWebComponent<GzTextareaWebComponent>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-title
      class="content"
      searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ContentControl].label}"
      ><span>${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ContentControl].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="exchange-content-control-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
      >${translateFn(
        'The Content Control module enhances email protection by filtering all email traffic that does not comply with company policies, such as unwanted or potentially sensitive content.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <gz-form-field id="exchange-content-control-enable-field" class="mt-4 mbw-1" status-field>
        <gz-toggle
          class="content-title-toggle"
          searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ContentFiltering].label}"
          gz-form-input
          @toggleChange="${this.onToggleChange}"
        >
          <span
            >${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ContentFiltering].translatedLabel}</span
          >
        </gz-toggle>
      </gz-form-field>

      <div id="exchange-content-control-content">
        <gz-text
          id="exchange-content-control-content-filtering-text"
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="infoText"
          >${translateFn('Content Filtering scans email traffic for defined keywords in the subject and body text.')}
        </gz-text>

        <!-- exchange content control rules -->
        <gz-form-field id="exchange-content-control-rules-field" status-field>
          <gz-policy-panel-exchange-content-control-content-filtering-rules-grid
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-exchange-content-control-content-filtering-rules-grid>
        </gz-form-field>

        <div id="exchange-content-filtering-exclusions-content">
          ${this.renderExclusionsContent(
            this.searchableKeywords[ExchangeContentControlSearchableKeywords.Exclusions],
            translateFn('Define exclusions to bypass content filtering for specific senders or recipients.'),
            'content'
          )}
        </div>
      </div>

      <div>
        <gz-form-field id="exchange-content-control-attachment-enable-field" class="mt-4 mbw-1" status-field>
          <gz-toggle
            class="content-title-toggle"
            searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.AttachmentFiltering]
              .label}"
            gz-form-input
            @toggleChange="${e => toggleDisabled(this.contentControlAttachmentContent, !e.detail.checked)}"
          >
            <span
              >${this.searchableKeywords[ExchangeContentControlSearchableKeywords.AttachmentFiltering]
                .translatedLabel}</span
            >
          </gz-toggle>
        </gz-form-field>

        <div id="exchange-content-control-attachment-content">
          <gz-text
            id="exchange-antimalware-attachment-filtering-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'Attachment Filtering analyzes email attachments based on specific name patterns and file types.'
            )}
          </gz-text>

          <!-- exchange content control rules -->
          <gz-form-field id="exchange-content-control-attachment-rules-field" status-field>
            <gz-policy-panel-exchange-content-control-attachment-filtering-rules-grid
              class="removeGridPadding"
              gz-form-input
            ></gz-policy-panel-exchange-content-control-attachment-filtering-rules-grid>
          </gz-form-field>

          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-5 subtitle"
            searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.Settings].label}"
            >${this.searchableKeywords[ExchangeContentControlSearchableKeywords.Settings].translatedLabel}</gz-title
          >
          <gz-title
            fontWeight="${FontWeights.LIGHT}"
            fontSize="${FontSizes.PARAGRAPH_13}"
            class="mt-3"
            searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ReplacementText]
              .label}"
            >${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ReplacementText]
              .translatedLabel}</gz-title
          >
          <gz-form-field id="exchange-attachment-settings-replacement-text-field" class="mt-1">
            <gz-textarea
              gz-form-input
              allowBeyondMaxChars
              .maxChars=${MAX_CHARS_SUPPORTED}
              placeholder="${translateFn('Type here')}"
            ></gz-textarea>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <div id="exchange-attachment-filtering-exclusions-content">
            ${this.renderExclusionsContent(
              this.searchableKeywords[ExchangeContentControlSearchableKeywords.Exclusions],
              translateFn('Define exclusions to bypass attachment filtering for specific senders or recipients.'),
              'attachment'
            )}
          </div>
        </div>
      </div>
    `;
  }

  private renderExclusionsContent(title: SearchableKeyword, description: string, source: string): TemplateResult {
    return html`
      <gz-title
        id="${'exchange-' + source + '-filtering-exclusions-title'}"
        fontWeight="${FontWeights.SEMIBOLD}"
        fontSize="${FontSizes.PARAGRAPH_12}"
        class="mt-5 subtitle"
        searchable-label="${title.label}"
        >${title.translatedLabel}</gz-title
      >
      <gz-text
        id="${'exchange-' + source + '-filtering-exclusions-text'}"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        class="infoText mt-1"
        >${description}
      </gz-text>

      <gz-title
        id="${'exchange-' + source + '-filtering-exclusions-trusted-title'}"
        fontWeight="${FontWeights.LIGHT}"
        fontSize="${FontSizes.HEADING_6}"
        class="mt-2"
        searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.TrustedSenders].label}"
        >${this.searchableKeywords[ExchangeContentControlSearchableKeywords.TrustedSenders].translatedLabel}</gz-title
      >

      <gz-form-field class="label-mw-250 mt-1" id="${'exchange-' + source + '-filtering-exclusions-senders-field'}">
        <gz-chips-generator-input
          gz-form-input
          hasFloatingLabel
          .validators=${[validateEmailPattern]}
          .strings=${{
            label: translateFn('Email addresses or domains'),
            placeholder: translateFn('Enter email addresses or domain patterns'),
            hintText: translateFn('Use a comma to separate multiple entries.'),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
            clearListButtonText: translateFn('Clear list'),
            searchPlaceholder: translateFn('Search'),
            noSearchResultsString: translateFn('No results found'),
            deleteWindowTitle: translateFn('Remove email addresses and domains?'),
            deleteWindowContentText: translateFn(
              'You are about to remove all email addresses and domains from the list. This action cannot be undone.'
            ),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Remove'),
          }}
        >
        </gz-chips-generator-input>
      </gz-form-field>

      <gz-title
        id="${'exchange-' + source + '-filtering-exclusions-recipients-title'}"
        fontWeight="${FontWeights.LIGHT}"
        fontSize="${FontSizes.HEADING_6}"
        class="mt-3"
        searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.TrustedRecipients].label}"
        >${this.searchableKeywords[ExchangeContentControlSearchableKeywords.TrustedRecipients]
          .translatedLabel}</gz-title
      >

      <gz-form-field class="label-mw-250 mt-1" id="${'exchange-' + source + '-filtering-exclusions-recipients-field'}">
        <gz-chips-generator-input
          gz-form-input
          hasFloatingLabel
          .validators=${[validateEmailPattern]}
          .strings=${{
            label: translateFn('Email addresses or domains'),
            placeholder: translateFn('Enter email addresses or domain patterns'),
            hintText: translateFn('Use a comma to separate multiple entries.'),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
            clearListButtonText: translateFn('Clear list'),
            searchPlaceholder: translateFn('Search'),
            noSearchResultsString: translateFn('No results found'),
            deleteWindowTitle: translateFn('Remove email addresses and domains?'),
            deleteWindowContentText: translateFn(
              'You are about to remove all email addresses and domains from the list. This action cannot be undone.'
            ),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Remove'),
          }}
        >
        </gz-chips-generator-input>
      </gz-form-field>

      <gz-form-field id="${'exchange-' + source + '-filtering-exclusions-exclude-field'}">
        <gz-checkbox
          searchable-label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ExcludeEmail].label}"
          gz-form-input
          label="${this.searchableKeywords[ExchangeContentControlSearchableKeywords.ExcludeEmail].translatedLabel}"
        ></gz-checkbox>
      </gz-form-field>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.exchange.contentControl
   */
  protected getValueFromManager(): ExchangeContentControlFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: {
          [SectionId.EXCHANGE_CONTENT_CONTROL]: EXCHANGE_CONTENT_CONTROL_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.EXCHANGE_PROTECTION]: {
          [SectionId.EXCHANGE_CONTENT_CONTROL]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.exchange.contentControl'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        exchange: {
          contentControl: {
            attachmentFiltering: {
              enable: this.contentControlAttachmentEnableField,
              settings: {
                replacementText: this.attachmentSettingsReplacementTextField,
              },
              exclusions: {
                trustedSenders: this.attachmentFilteringExclusionsSendersField,
                trustedRecipients: this.attachmentFilteringExclusionsRecipientsField,
                excludeOnlyIfAllRecipientsAreTrusted: this.attachmentFilteringExclusionsExcludeField,
              },
              rules: this.contentControlAttachmentRulesField,
            },
            contentFiltering: {
              enable: this.contentControlEnableField,
              exclusions: {
                trustedSenders: this.contentFilteringExclusionsSendersField,
                trustedRecipients: this.contentFilteringExclusionsRecipientsField,
                excludeOnlyIfAllRecipientsAreTrusted: this.contentFilteringExclusionsExcludeField,
              },
              rules: this.contentControlRulesField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        exchange: {
          contentControl: this.valueT.uiSettings.exchange.contentControl,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // get paths of exclusions
    const attachmentFilteringExclusionsPath = [
      'uiSettings',
      'exchange',
      'contentControl',
      'attachmentFiltering',
      'exclusions',
    ];
    const contentFilteringExclusionsPath = [
      'uiSettings',
      'exchange',
      'contentControl',
      'contentFiltering',
      'exclusions',
    ];

    // get exclusions
    const attachmentFilteringExclusionsSenders = _.get(
      value,
      [...attachmentFilteringExclusionsPath, 'trustedSenders'],
      []
    );
    const attachmentFilteringExclusionsRecipients = _.get(
      value,
      [...attachmentFilteringExclusionsPath, 'trustedRecipients'],
      []
    );
    const contentFilteringExclusionsSenders = _.get(value, [...contentFilteringExclusionsPath, 'trustedSenders'], []);
    const contentFilteringExclusionsRecipients = _.get(
      value,
      [...contentFilteringExclusionsPath, 'trustedRecipients'],
      []
    );

    // sort arrays of exclusions
    _.set(
      value,
      [...attachmentFilteringExclusionsPath, 'trustedSenders'],
      sortEmailsAndWildcards(attachmentFilteringExclusionsSenders)
    );
    _.set(
      value,
      [...attachmentFilteringExclusionsPath, 'trustedRecipients'],
      sortEmailsAndWildcards(attachmentFilteringExclusionsRecipients)
    );
    _.set(
      value,
      [...contentFilteringExclusionsPath, 'trustedSenders'],
      sortEmailsAndWildcards(contentFilteringExclusionsSenders)
    );
    _.set(
      value,
      [...contentFilteringExclusionsPath, 'trustedRecipients'],
      sortEmailsAndWildcards(contentFilteringExclusionsRecipients)
    );

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'exchange', 'contentControl'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.contentControlContent, !event.detail.checked);
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();

    // reset grids
    this.contentControlRulesField.Input.reset();
    this.contentControlAttachmentRulesField.Input.reset();

    // reset input of chips generator
    let chipsGeneratorFormField = this.contentFilteringExclusionsSendersField.Input.shadowRoot?.querySelector(
      '#chips-generator-form-field'
    ) as GzFormFieldWebComponent<GzInputWebComponent, string>;
    chipsGeneratorFormField?.Input?.setFormValue('');

    chipsGeneratorFormField = this.contentFilteringExclusionsRecipientsField.Input.shadowRoot?.querySelector(
      '#chips-generator-form-field'
    ) as GzFormFieldWebComponent<GzInputWebComponent, string>;
    chipsGeneratorFormField?.Input?.setFormValue('');

    chipsGeneratorFormField = this.attachmentFilteringExclusionsSendersField.Input.shadowRoot?.querySelector(
      '#chips-generator-form-field'
    ) as GzFormFieldWebComponent<GzInputWebComponent, string>;
    chipsGeneratorFormField?.Input?.setFormValue('');

    chipsGeneratorFormField = this.attachmentFilteringExclusionsRecipientsField.Input.shadowRoot?.querySelector(
      '#chips-generator-form-field'
    ) as GzFormFieldWebComponent<GzInputWebComponent, string>;
    chipsGeneratorFormField?.Input?.setFormValue('');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL: GzExchangeContentControlWebComponent;
  }
}
